import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { SummaryCardData } from "store/types";

interface Props {
  data?: SummaryCardData;
  loading?: boolean;
}

export const SummaryCard: React.FC<Props> = ({ data, loading }) => {
  const { widthPercent } = useCardSize();
  const isCompact = widthPercent <= 50;

  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const items = [
    { label: "Total", value: data.total },
    { label: "Average", value: data.average },
    { label: "Min", value: data.min },
    { label: "Max", value: data.max }
  ].filter((item) => item.value !== undefined && item.value !== null);

  if (items.length === 0) {
    return (
      <Box sx={{ p: isCompact ? "12px 14px" : "20px 24px" }}>
        {loading && <LinearProgress sx={{ mb: "4px" }} />}
        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No summary data available</Typography>
      </Box>
    );
  }

  const title = data.measureName || data.parameter || data.title || "Summary";

  return (
    <Box sx={{ p: isCompact ? "12px 14px" : "20px 24px", height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}
      <Typography
        sx={{
          fontSize: isCompact ? 10 : 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: isCompact ? "8px" : "12px"
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr 1fr" : `repeat(${items.length}, 1fr)`,
          gap: isCompact ? "10px 12px" : "14px 20px",
          flex: 1,
          alignContent: "start"
        }}
      >
        {items.map((item) => (
          <Box key={item.label} sx={{ borderLeft: "3px solid #1A3C34", pl: isCompact ? "8px" : "10px", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: isCompact ? 9 : 10,
                fontWeight: 600,
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: "3px"
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontSize: isCompact ? 14 : 18,
                fontWeight: 700,
                color: "#111827",
                wordBreak: "break-word"
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
